import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

// Validation schema for the form
const formSchema = z
  .object({
    email: z.string().email("Please enter a valid email address"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof formSchema>;

interface NannyRegistrationResponse {
  success: boolean;
  message: string;
  error?: string;
}

const NannyRegister = () => {
  const { inviteCode } = useParams<{ inviteCode?: string }>(); // Mark as optional
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [inviteValid, setInviteValid] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  // Validate the invite code on mount
  useEffect(() => {
    if (!inviteCode) {
      toast({
        title: "Invalid Invite",
        description: "No invitation code provided.",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }
    const validateInvite = async () => {
      try {
        const response = await ApiService.request<{ valid: boolean }>(
          EndpointKeys.VALIDATE_NANNY_INVITE,
          {
            urlParams: { code: inviteCode },
          },
        );

        setInviteValid(response.valid);
        if (!response.valid) {
          toast({
            title: "Invalid Invite",
            description: "This invitation link is invalid or has expired.",
            variant: "destructive",
          });
          navigate("/login");
        }
      } catch (error) {
        console.error("Error validating invite:", error);
        toast({
          title: "Error",
          description: "Failed to validate invitation.",
          variant: "destructive",
        });
        navigate("/login");
      }
    };

    if (inviteCode) {
      validateInvite();
    }
  }, [inviteCode, navigate, toast]);

  const onSubmit = async (data: FormData) => {
    if (!inviteValid || !inviteCode) return;

    setIsLoading(true);
    try {
      const response = await ApiService.request<NannyRegistrationResponse>(
        EndpointKeys.REGISTER_NANNY,
        {
          method: "POST",
          body: {
            email: data.email,
            password: data.password,
            inviteCode: inviteCode,
          },
        },
      );

      if (response.success) {
        toast({
          title: "Registration Successful",
          description: "You can now log in to your account.",
        });
        navigate("/login");
      } else {
        throw new Error(response.error || "Registration failed");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Registration failed",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!inviteValid) {
    return null; // Or a loading state if you prefer
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Complete Your Registration</CardTitle>
          <CardDescription>
            Create your account to get started with the Nanny Pay Portal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Create a password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Confirm your password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Registering..." : "Register"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default NannyRegister;
